/* eslint-disable import/no-named-as-default-member */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { uniqBy } from 'lodash'
import { useEffect, useState } from 'react'

import { ErrorProps } from './common'
import useNextToken from './useNextToken'
import { DEFAULT_ENTRIES_PER_PAGE } from '../constants/api'
import { fetchQuery } from '../utils/ApiUtils'

interface QueryInfo {
  name: string
  query: any
  options: any
  isFP?: boolean
}

/**
 * Shared paginated events fetcher. Handles recursive fetch, token management,
 * uniqBy dedup, loading/error state, and optional per-item parsing.
 */
export function usePaginatedEvents<T>(
  queryInfo: QueryInfo[],
  parseItem?: (item: T) => T,
) {
  const [items, setItems] = useState<T[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<ErrorProps | null>()

  const { setNewToken, startUsingToken, onTokenSuccess } = useNextToken()

  useEffect(() => {
    let didCancel = false
    let allItems: any[] = []
    let currentNextToken: string | null = null
    let queryCount = 0

    const fetchData = async () => {
      if (didCancel) return
      if (!currentNextToken && queryCount === queryInfo.length) {
        setLoading(false)
        return
      }

      if (queryCount < queryInfo.length) {
        setLoading(true)

        if (currentNextToken) startUsingToken(currentNextToken)

        try {
          const { items: batch, nextToken: newNextToken } = (await fetchQuery(
            queryInfo[queryCount].name,
            queryInfo[queryCount].query,
            {
              limit: DEFAULT_ENTRIES_PER_PAGE,
              ...(currentNextToken && { nextToken: currentNextToken }),
              ...queryInfo[queryCount].options,
            },
          )) as unknown as { items: any[]; nextToken: any }

          const processed = queryInfo[queryCount].isFP
            ? batch.reduce(
                (prev, cur) => prev.concat({ ...cur, isFP: true }),
                [],
              )
            : batch

          allItems = uniqBy([...allItems, ...processed], 'id')

          if (currentNextToken) onTokenSuccess(currentNextToken)

          if (parseItem) {
            setItems(allItems.map(parseItem))
          } else {
            setItems([...allItems])
          }

          currentNextToken = newNextToken
          if (newNextToken) {
            setNewToken(newNextToken)
            fetchData()
          } else {
            queryCount++
            setNewToken(null)
            fetchData()
          }
        } catch (e) {
          console.log(e)
          setError({ title: 'Fail to load events', message: '' })
        }
      }
    }

    fetchData()
    return () => {
      didCancel = true
    }
  }, [setNewToken])

  return { items, apiLoading: loading, apiError: error }
}
